import { getBackendUrl } from "@/lib/backend";
import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  RefreshCw, IndianRupee, Cpu, Wallet, Users, TrendingUp,
  Receipt, AlertCircle, Sparkles, Mail, Server, Box,
} from "lucide-react";

const API = `${getBackendUrl()}/api`;

const PERIODS = [
  { id: "today", label: "Today" },
  { id: "7d",    label: "7 days" },
  { id: "30d",   label: "30 days" },
  { id: "90d",   label: "90 days" },
  { id: "all",   label: "All time" },
];

function fmtINR(n) {
  if (n == null || Number.isNaN(n)) return "—";
  return "₹" + Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 });
}
function fmtUSD(n) {
  if (n == null || Number.isNaN(n)) return "—";
  return "$" + Number(n).toLocaleString("en-US", { maximumFractionDigits: 2 });
}
function fmtNum(n) {
  if (n == null || Number.isNaN(n)) return "0";
  return Number(n).toLocaleString("en-IN");
}

function StatCard({ title, value, sub, icon: Icon, accent = false, tone, testId }) {
  const toneCls = tone === "danger"
    ? "border-rose-400/30 bg-rose-400/5"
    : tone === "good"
    ? "border-emerald-400/30 bg-emerald-400/5"
    : accent
    ? "border-[#D4AF37]/35 bg-gradient-to-br from-[#D4AF37]/8 via-[#1a1306] to-[#0E0E0E]"
    : "border-white/8 bg-[#0E0E0E]";
  return (
    <div data-testid={testId} className={`rounded-2xl border p-5 ${toneCls}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="text-[10px] uppercase tracking-[0.22em] text-white/45">{title}</div>
        {Icon && <Icon className={`w-4 h-4 ${accent ? "text-[#D4AF37]" : "text-white/35"}`} />}
      </div>
      <div className="mt-2 flex items-baseline gap-2">
        <span className={`font-display tracking-tight text-3xl sm:text-4xl leading-none ${accent ? "text-[#D4AF37]" : "text-white"}`}>
          {value}
        </span>
      </div>
      {sub && <div className="mt-2 text-[11px] text-white/55">{sub}</div>}
    </div>
  );
}

function Row({ label, value, hint }) {
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-white/5 last:border-0">
      <div>
        <div className="text-sm text-white/80">{label}</div>
        {hint && <div className="text-[11px] text-white/45 mt-0.5">{hint}</div>}
      </div>
      <div className="text-sm text-white font-medium tabular-nums">{value}</div>
    </div>
  );
}

export default function AdminCostDashboard({ authHeader, onUnauthorized }) {
  const [period, setPeriod] = useState("30d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (p = period) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/metrics/cost-dashboard`, {
        headers: authHeader, params: { period: p },
      });
      setData(res.data);
    } catch (err) {
      if (err?.response?.status === 401) return onUnauthorized?.();
      toast.error("Could not load cost dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(period); /* eslint-disable-line */ }, [period]);

  const marginTone = useMemo(() => {
    if (!data) return undefined;
    const p = data.margin?.gross_margin_pct ?? 0;
    if (data.revenue?.gross_inr === 0) return undefined;
    if (p >= 70) return "good";
    if (p < 40) return "danger";
    return undefined;
  }, [data]);

  return (
    <div data-testid="admin-cost-dashboard" className="space-y-6">
      {/* Period selector */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          {PERIODS.map((p) => (
            <button
              key={p.id}
              onClick={() => setPeriod(p.id)}
              data-testid={`cost-period-${p.id}`}
              className={`text-xs px-3 py-1.5 rounded-full border transition ${
                period === p.id
                  ? "border-[#D4AF37] bg-[#D4AF37]/15 text-[#D4AF37]"
                  : "border-white/10 text-white/65 hover:bg-white/5"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <button
          onClick={() => load(period)}
          data-testid="cost-refresh-btn"
          className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-lg border border-white/10 hover:bg-white/5 transition self-start sm:self-auto"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {!data ? (
        <div className="text-center text-white/45 text-sm py-16">
          {loading ? "Loading metrics…" : "No data."}
        </div>
      ) : (
        <>
          {/* Hero KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <StatCard
              testId="kpi-revenue"
              title="Gross Revenue"
              value={fmtINR(data.revenue.gross_inr)}
              sub={`${data.revenue.successful_payments} successful payment(s)`}
              icon={IndianRupee}
              accent
            />
            <StatCard
              testId="kpi-net"
              title="Net (after Razorpay)"
              value={fmtINR(data.revenue.net_inr)}
              sub={`Fees ~${data.revenue.razorpay_fee_pct}% (${fmtINR(data.revenue.razorpay_fees_inr)})`}
              icon={Receipt}
            />
            <StatCard
              testId="kpi-llm-cost"
              title="LLM Spend"
              value={fmtINR(data.llm.est_cost_inr)}
              sub={`≈ ${fmtUSD(data.llm.est_cost_usd)} · ${fmtNum(data.llm.image_calls)} image calls`}
              icon={Cpu}
            />
            <StatCard
              testId="kpi-tripo-cost"
              title="Tripo3D Spend"
              value={fmtINR(data.tripo.est_cost_inr)}
              sub={`≈ ${fmtUSD(data.tripo.est_cost_usd)} · ${fmtNum(data.tripo.succeeded)} successful run(s)`}
              icon={Box}
            />
            <StatCard
              testId="kpi-margin"
              title="Gross Margin"
              value={data.revenue.gross_inr > 0 ? `${data.margin.gross_margin_pct}%` : "—"}
              sub={data.revenue.gross_inr > 0
                ? `${fmtINR(data.margin.gross_margin_inr)} after variable costs`
                : "No revenue yet"}
              icon={TrendingUp}
              tone={marginTone}
            />
          </div>

          {/* Detail panels */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5">
            {/* Revenue + fees */}
            <div className="rounded-2xl border border-white/8 bg-[#0E0E0E] p-5" data-testid="panel-revenue">
              <div className="flex items-center gap-2 mb-4">
                <Receipt className="w-4 h-4 text-[#D4AF37]" />
                <h3 className="text-sm font-medium tracking-wide">Revenue · Razorpay</h3>
              </div>
              <Row label="Gross revenue" value={fmtINR(data.revenue.gross_inr)} />
              <Row label="Razorpay fees (estimate)" value={`− ${fmtINR(data.revenue.razorpay_fees_inr)}`} hint={`${data.revenue.razorpay_fee_pct}% incl. GST`} />
              <Row label="Net revenue" value={fmtINR(data.revenue.net_inr)} />
              <Row label="Successful payments" value={fmtNum(data.revenue.successful_payments)} />
              <Row label="Avg order value" value={fmtINR(data.revenue.avg_order_value_inr)} />
              <Row label="Credits sold" value={fmtNum(data.revenue.credits_sold)} />
            </div>

            {/* LLM cost */}
            <div className="rounded-2xl border border-white/8 bg-[#0E0E0E] p-5" data-testid="panel-llm">
              <div className="flex items-center gap-2 mb-4">
                <Cpu className="w-4 h-4 text-[#D4AF37]" />
                <h3 className="text-sm font-medium tracking-wide">LLM Usage · Variable Cost</h3>
              </div>
              <Row
                label="Image generations (generate)"
                value={fmtNum(data.llm.by_action.studio_generate)}
                hint={`× ${fmtUSD(data.llm.cost_per_image_usd)} each`}
              />
              <Row
                label="Multi-view renders"
                value={fmtNum(data.llm.by_action.studio_multiview)}
              />
              <Row
                label="Refine renders"
                value={fmtNum(data.llm.by_action.studio_refine)}
              />
              <Row
                label="CAD exports"
                value={fmtNum(data.llm.by_action.studio_cad)}
                hint="No LLM cost (pure compute)"
              />
              <Row
                label="BOM estimates (Gemini Flash)"
                value={fmtNum(data.llm.bom_estimate_calls)}
                hint={`× ${fmtUSD(data.llm.cost_per_bom_usd)} each`}
              />
              <div className="mt-3 pt-3 border-t border-white/10 flex items-center justify-between">
                <div className="text-sm text-white/75">Total LLM spend</div>
                <div className="text-sm font-medium text-[#D4AF37] tabular-nums">
                  {fmtINR(data.llm.est_cost_inr)} <span className="text-white/45 text-xs">({fmtUSD(data.llm.est_cost_usd)})</span>
                </div>
              </div>
            </div>

            {/* Tripo3D cost & revenue attribution */}
            <div className="rounded-2xl border border-white/8 bg-[#0E0E0E] p-5" data-testid="panel-tripo">
              <div className="flex items-center gap-2 mb-4">
                <Box className="w-4 h-4 text-[#D4AF37]" />
                <h3 className="text-sm font-medium tracking-wide">Tripo3D · 2D → 3D CAD</h3>
              </div>
              <Row
                label="Successful runs (billable to us)"
                value={fmtNum(data.tripo.succeeded)}
                hint={`× ${fmtUSD(data.tripo.cost_per_run_usd)} each`}
              />
              <Row label="Failed / cancelled / expired" value={fmtNum((data.tripo.failed || 0) + (data.tripo.cancelled || 0) + (data.tripo.expired || 0))} hint="Refunded — no Tripo charge" />
              <Row label="In-flight (queued + running)" value={fmtNum(data.tripo.running)} />
              <Row label="Credits charged (gross)" value={fmtNum(data.tripo.credits_charged_gross)} />
              <Row label="Credits refunded" value={`− ${fmtNum(data.tripo.credits_refunded)}`} />
              <Row
                label="Revenue attributed (estimate)"
                value={fmtINR(data.tripo.revenue_inr_attributed)}
                hint={data.tripo.avg_inr_per_credit > 0
                  ? `${fmtNum(data.tripo.credits_net_billed)} net credits × ${fmtINR(data.tripo.avg_inr_per_credit)} / credit`
                  : "Awaiting first paid credit sale to derive credit price"}
              />
              <div className="mt-3 pt-3 border-t border-white/10 flex items-center justify-between">
                <div className="text-sm text-white/75">Tripo gross margin</div>
                <div className="text-sm font-medium tabular-nums">
                  <span className={data.tripo.margin_inr >= 0 ? "text-emerald-300" : "text-rose-300"}>
                    {fmtINR(data.tripo.margin_inr)}
                  </span>
                  {data.tripo.revenue_inr_attributed > 0 && (
                    <span className="text-white/45 text-xs ml-2">({data.tripo.margin_pct}%)</span>
                  )}
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-white/10 flex items-center justify-between">
                <div className="text-sm text-white/75">Tripo cost</div>
                <div className="text-sm font-medium text-[#D4AF37] tabular-nums">
                  {fmtINR(data.tripo.est_cost_inr)} <span className="text-white/45 text-xs">({fmtUSD(data.tripo.est_cost_usd)})</span>
                </div>
              </div>
            </div>

            {/* Users */}
            <div className="rounded-2xl border border-white/8 bg-[#0E0E0E] p-5" data-testid="panel-users">
              <div className="flex items-center gap-2 mb-4">
                <Users className="w-4 h-4 text-[#D4AF37]" />
                <h3 className="text-sm font-medium tracking-wide">Users</h3>
              </div>
              <Row label="Total users" value={fmtNum(data.users.total)} />
              <Row label="Free" value={fmtNum(data.users.free)} />
              <Row label="Edu (CAD-restricted)" value={fmtNum(data.users.edu)} />
              <Row label="Paid" value={fmtNum(data.users.paid)} />
              <Row label="Active in period" value={fmtNum(data.users.active_in_period)} hint=">=1 generation" />
              <Row label="New signups in period" value={fmtNum(data.users.new_signups_in_period)} />
            </div>

            {/* Credits in circulation */}
            <div className="rounded-2xl border border-white/8 bg-[#0E0E0E] p-5" data-testid="panel-credits">
              <div className="flex items-center gap-2 mb-4">
                <Wallet className="w-4 h-4 text-[#D4AF37]" />
                <h3 className="text-sm font-medium tracking-wide">Credits in Circulation</h3>
              </div>
              <Row label="Granted (in period)" value={fmtNum(data.credits.granted_in_period)} />
              <Row label="Spent (in period)" value={fmtNum(data.credits.spent_in_period)} />
              <Row label="Outstanding (all users, current)" value={fmtNum(data.credits.outstanding_balance_total)} hint="Future LLM liability" />
              {Object.keys(data.credits.grant_breakdown || {}).length > 0 && (
                <div className="mt-3 pt-3 border-t border-white/10">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-2">Grants by source</div>
                  {Object.entries(data.credits.grant_breakdown).map(([reason, val]) => (
                    <Row
                      key={reason}
                      label={reason.replace(/_/g, " ")}
                      value={`+${fmtNum(val)}`}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Platform fixed */}
            <div className="rounded-2xl border border-white/8 bg-[#0E0E0E] p-5" data-testid="panel-platform">
              <div className="flex items-center gap-2 mb-4">
                <Server className="w-4 h-4 text-[#D4AF37]" />
                <h3 className="text-sm font-medium tracking-wide">Platform · Fixed Monthly</h3>
              </div>
              <Row
                label="Emergent deployment"
                value={`${fmtINR(data.platform.deploy_inr_per_month)} / mo`}
                hint={`${fmtUSD(data.platform.deploy_usd_per_month)} · 50 credits`}
              />
              <Row
                label="Resend (transactional email)"
                value={data.platform.resend_in_free_tier
                  ? "Free tier"
                  : `${fmtUSD(data.platform.resend_usd_pro_if_exceeded)} / mo`}
                hint={`~${fmtNum(data.platform.resend_emails_est)} emails est. · free ≤ ${fmtNum(data.platform.resend_free_tier_monthly)}/mo`}
              />
              <Row
                label="MongoDB"
                value="Free"
                hint="Bundled with deployment"
              />
              <Row
                label="SSL · Domain routing"
                value="Free"
                hint="Handled by Emergent"
              />
            </div>

            {/* Margin breakdown */}
            <div className="rounded-2xl border border-[#D4AF37]/25 bg-gradient-to-br from-[#1a1306] via-[#0E0E0E] to-[#0E0E0E] p-5" data-testid="panel-margin">
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp className="w-4 h-4 text-[#D4AF37]" />
                <h3 className="text-sm font-medium tracking-wide">Profitability Snapshot</h3>
              </div>
              <Row label="Net revenue" value={fmtINR(data.margin.revenue_net_inr)} />
              <Row label="− Variable LLM cost" value={`− ${fmtINR(data.margin.variable_cost_inr)}`} />
              <div className="flex items-center justify-between py-3 border-t border-white/10 mt-1">
                <div className="text-sm text-white">Gross margin</div>
                <div className="text-base font-medium tabular-nums">
                  <span className={data.margin.gross_margin_inr >= 0 ? "text-emerald-300" : "text-rose-300"}>
                    {fmtINR(data.margin.gross_margin_inr)}
                  </span>
                  {data.revenue.gross_inr > 0 && (
                    <span className="text-white/55 text-xs ml-2">({data.margin.gross_margin_pct}%)</span>
                  )}
                </div>
              </div>
              <Row
                label="Fixed monthly platform cost"
                value={fmtINR(data.margin.fixed_cost_monthly_inr)}
                hint="Deploy + Resend (when applicable)"
              />

              {data.revenue.gross_inr === 0 && (
                <div className="mt-4 flex items-start gap-2 text-xs text-white/55 bg-white/5 rounded-lg p-3">
                  <AlertCircle className="w-3.5 h-3.5 mt-0.5 text-[#D4AF37] flex-shrink-0" />
                  <span>No revenue yet in this period. Once payments flow in, margin % will populate automatically.</span>
                </div>
              )}
            </div>
          </div>

          {/* Footer config note */}
          <div className="text-[11px] text-white/35 px-1">
            <Sparkles className="w-3 h-3 inline -mt-0.5 mr-1" />
            Estimates use:
            FX <strong className="text-white/55">1 USD = ₹{data.config.usd_to_inr}</strong> ·
            Razorpay fee <strong className="text-white/55">{data.config.razorpay_fee_pct}%</strong> ·
            Nano Banana <strong className="text-white/55">{fmtUSD(data.config.cost_per_image_usd)}/image</strong> ·
            Gemini Flash <strong className="text-white/55">{fmtUSD(data.config.cost_per_bom_usd)}/BOM</strong> ·
            Tripo3D <strong className="text-white/55">{fmtUSD(data.config.cost_per_tripo_run_usd)}/run</strong>.
            Tune via env vars (<code className="text-[#D4AF37]">COST_NANO_BANANA_PER_IMAGE_USD</code>, <code className="text-[#D4AF37]">COST_TRIPO_PER_RUN_USD</code>, <code className="text-[#D4AF37]">USD_TO_INR</code>, etc.).
          </div>
        </>
      )}
    </div>
  );
}
